import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Building2, Plus, Loader2, MapPin, Megaphone, Image } from "lucide-react";
import { format } from "date-fns";

const tierColors = {
  BRONZE: "tier-bronze border",
  SILVER: "tier-silver border",
  GOLD: "tier-gold border"
};

const statusColors = {
  ACTIVE: "status-active",
  SUSPENDED: "text-destructive bg-destructive/10 border-destructive/30",
  TRIAL: "status-paused",
  PENDING_VERIFICATION: "status-draft"
};

const tiers = ["BRONZE", "SILVER", "GOLD"];
const statuses = ["ACTIVE", "SUSPENDED", "TRIAL", "PENDING_VERIFICATION"];

export default function AdminAccounts() {
  const { token } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    business_name: "",
    abn: "",
    tier: "BRONZE",
    primary_contact: {
      name: "",
      email: "",
      phone: "",
      role: "Marketing",
      is_emergency_contact: false
    }
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data);
    } catch (error) {
      toast.error("Failed to fetch accounts");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/accounts`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Account created successfully");
      setDialogOpen(false);
      resetForm();
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create account");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTier = async (accountId, newTier) => {
    try {
      await axios.put(`${API}/accounts/${accountId}`, { tier: newTier }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Tier updated");
      fetchAccounts();
    } catch (error) {
      toast.error("Failed to update tier");
    }
  };

  const handleUpdateStatus = async (accountId, newStatus) => {
    try {
      await axios.put(`${API}/accounts/${accountId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Status updated");
      fetchAccounts();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const resetForm = () => {
    setFormData({
      business_name: "",
      abn: "",
      tier: "BRONZE",
      primary_contact: {
        name: "",
        email: "",
        phone: "",
        role: "Marketing",
        is_emergency_contact: false
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading accounts...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-accounts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Advertiser Accounts</h1>
          <p className="text-muted-foreground mt-1">
            Manage all advertiser accounts on the platform
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="create-account-btn">
              <Plus className="mr-2 h-4 w-4" />
              Create Account
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Create New Advertiser Account</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_name">Business Name</Label>
                  <Input
                    id="business_name"
                    value={formData.business_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                    required
                    data-testid="account-business-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="abn">ABN</Label>
                  <Input
                    id="abn"
                    value={formData.abn}
                    onChange={(e) => setFormData(prev => ({ ...prev, abn: e.target.value }))}
                    placeholder="XX XXX XXX XXX"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tier</Label>
                <Select value={formData.tier} onValueChange={(value) => setFormData(prev => ({ ...prev, tier: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tiers.map(tier => (
                      <SelectItem key={tier} value={tier}>{tier}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4 p-4 rounded-lg bg-secondary/20 border border-border">
                <h4 className="font-semibold">Primary Contact</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Contact Name</Label>
                    <Input
                      value={formData.primary_contact.name}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        primary_contact: { ...prev.primary_contact, name: e.target.value }
                      }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.primary_contact.email}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        primary_contact: { ...prev.primary_contact, email: e.target.value }
                      }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.primary_contact.phone}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        primary_contact: { ...prev.primary_contact, phone: e.target.value }
                      }))}
                      placeholder="+61 4XX XXX XXX"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Input
                      value={formData.primary_contact.role}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        primary_contact: { ...prev.primary_contact, role: e.target.value }
                      }))}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-account-btn">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Create Account
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Total Accounts</p>
                <p className="text-2xl font-bold mt-1">{accounts.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-primary/60" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Active</p>
                <p className="text-2xl font-bold mt-1">{accounts.filter(a => a.status === "ACTIVE").length}</p>
              </div>
              <div className="h-8 w-8 rounded-full bg-green-500/20 flex items-center justify-center">
                <div className="h-3 w-3 rounded-full bg-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Trial</p>
                <p className="text-2xl font-bold mt-1">{accounts.filter(a => a.status === "TRIAL").length}</p>
              </div>
              <div className="h-8 w-8 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <div className="h-3 w-3 rounded-full bg-yellow-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Accounts Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map(account => (
                  <TableRow key={account.id} data-testid={`account-row-${account.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-semibold">{account.business_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{account.abn}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{account.primary_contact?.name}</p>
                        <p className="text-xs text-muted-foreground">{account.primary_contact?.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select 
                        value={account.tier} 
                        onValueChange={(value) => handleUpdateTier(account.id, value)}
                      >
                        <SelectTrigger className="w-32">
                          <Badge className={tierColors[account.tier]}>{account.tier}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {tiers.map(tier => (
                            <SelectItem key={tier} value={tier}>{tier}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select 
                        value={account.status} 
                        onValueChange={(value) => handleUpdateStatus(account.id, value)}
                      >
                        <SelectTrigger className="w-40">
                          <Badge className={statusColors[account.status]}>{account.status}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {statuses.map(status => (
                            <SelectItem key={status} value={status}>{status.replace("_", " ")}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(account.created_at), "MMM d, yyyy")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
